import { inject, injectable } from 'inversify';

import { PiniaStore } from '@/decorators/store';
import { Connection } from '@/services/Connection';
import { Environment } from '@/services/Environment';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { orderByField } from '@/utils/orderBy';
import { IDropdownOption } from '~/IDropdownOption';

@injectable()
export abstract class AbstractEntityStore<TEntity extends Record<string, any>> extends PiniaStore {
  @inject(Connection) protected connection: Connection;
  @inject(Environment) protected environment: Environment;
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;

  protected entities: TEntity[] = [];

  protected entityIdKey: string = 'id';
  protected entityLabelKey: string = 'label';
  protected entityValueKey: string = 'value';

  get valueKey(): string {
    return this.entityValueKey;
  }

  [Symbol.toStringTag]() {
    return this.constructor.name;
  }

  all(): TEntity[] {
    return [...this.entities];
  }

  dropdownOptions(): IDropdownOption[] {
    const options = [...this.entities]
      .map((e) => <IDropdownOption> ({
        id: e[this.entityIdKey as keyof TEntity],
        label: e[this.entityLabelKey as keyof TEntity],
        value: e[this.entityValueKey as keyof TEntity],
      }));

    return orderByField<IDropdownOption>(options, this.entityLabelKey);
  }

  find(id?: string | number): TEntity | undefined {
    return this.entities.find((e) => this.getNestedValue(e, this.entityIdKey) === id);
  }

  set(entities: TEntity[]) {
    this.entities = [];
    this.update(...entities);
  }

  update(...entities: TEntity[]) {
    entities.forEach((entity) => {
      const id = this.getNestedValue(entity, this.entityIdKey) as string;
      const index = this.entities.findIndex((e) => this.getNestedValue(e, this.entityIdKey) === id);

      if (index === -1) {
        this.entities.push(entity);
      }
      else {
        this.entities.splice(index, 1, entity);
      }
    });
  }

  batchUpdate(entities: TEntity[]) {
    const updatedIds = new Map<string, TEntity>();
    entities.forEach((entity) => {
      const id = this.getNestedValue(entity, this.entityIdKey) as string;
      updatedIds.set(id, entity);
    });

    const merged = this.entities.map((existing) => {
      const id = this.getNestedValue(existing, this.entityIdKey) as string;
      const replacement = updatedIds.get(id);
      if (replacement) {
        updatedIds.delete(id);
        return replacement;
      }
      return existing;
    });

    // Append any new entities that didn't match an existing one.
    updatedIds.forEach((entity) => merged.push(entity));

    this.entities = merged;
  }

  toMap(): Map<string, TEntity> {
    const map = new Map<string, TEntity>();
    for (const entity of this.entities) {
      const id = this.getNestedValue(entity, this.entityIdKey) as string;
      map.set(id, entity);
    }
    return map;
  }

  forEach(callback: (entity: TEntity) => void) {
    this.entities.forEach(callback);
  }

  remove(id?: string | number) {
    const index = this.entities.findIndex((t) => this.getNestedValue(t, this.entityIdKey) === id);
    if (index === -1) return;
    this.entities.splice(index, 1);
  }

  private getNestedValue(obj: unknown, path: string): unknown {
    return path.split('.').reduce((acc, part) => acc && (acc as Record<string, any>)[part], obj);
  }
}
